import { useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { channelsPageData } from "@/mocks/homeData";
import { useEntranceAnimation } from "@/hooks/useEntranceAnimation";
import { StoresCollectionPageJsonLd, StoresItemListJsonLd, StoresBreadcrumbJsonLd } from "@/components/feature/JsonLd";
import { useState, useEffect } from "react";

function StoresHero() {
  const { hero } = channelsPageData;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section
      className="relative overflow-hidden flex items-center justify-center"
      style={{ minHeight: "100svh" }}
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://readdy.ai/api/search-image?query=Clean%20abstract%20network%20visualization%20for%20a%20book%20distribution%20technology%20company%2C%20elegant%20interconnected%20nodes%20and%20flowing%20pathway%20lines%20in%20soft%20silver%20gray%20and%20warm%20sand%20tones%20on%20bright%20off-white%20background%2C%20subtle%20geometric%20book%20shapes%20transforming%20into%20distribution%20network%20patterns%2C%20modern%20minimalist%20composition%20with%20airy%20spacious%20layout%2C%20purely%20abstract%20and%20non-photographic%2C%20light%20and%20professional%20atmosphere%20suitable%20for%20dark%20text%20overlay%2C%20sophisticated%20tech-meets-publishing%20aesthetic&width=1600&height=900&seq=qh-stores-brand-01&orientation=landscape&nocache=true')",
        }}
      />
      <div className="absolute inset-0 bg-white/20" />
      <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/50 via-black/15 to-transparent" />
      <div className="relative z-10 text-center px-4 w-full">
        <img
          src="https://storage.readdy-site.link/project_files/6517ebfd-0186-45e8-a53b-6a7dda87aef3/6d23f5d8-f2c4-4070-a39b-51782f265262_compressed_logo-02.webp"
          alt="千華駐科技"
          className={`h-10 md:h-12 w-auto mx-auto mb-5 object-contain transition-all duration-[600ms] ease-out ${
            mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
          style={{ transitionDelay: "0ms" }}
        />
        <h1
          className={`font-heading text-3xl md:text-4xl font-bold text-foreground-950 tracking-wide transition-all duration-[600ms] ease-out ${
            mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
          style={{ transitionDelay: "150ms" }}
        >
          {hero.title}
        </h1>
        <p
          className={`mt-3 text-sm md:text-base text-foreground-600 max-w-xl mx-auto leading-relaxed transition-all duration-[600ms] ease-out ${
            mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
          style={{ transitionDelay: "300ms" }}
        >
          {hero.subtitle}
        </p>
      </div>
    </section>
  );
}

function StoresSection() {
  const { platforms } = channelsPageData;
  const { ref: sectionRef, isVisible } = useEntranceAnimation({ threshold: 0.1, sectionIndex: 0 });

  return (
    <section
      ref={sectionRef}
      className="relative flex items-center justify-center bg-background-50 overflow-hidden"
      style={{ minHeight: "100svh" }}
    >
      {/* 裝飾性背景 */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-accent-100/20 blur-3xl -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[28rem] h-[28rem] rounded-full bg-secondary-100/20 blur-3xl translate-y-1/3 -translate-x-1/4" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-primary-50/10 blur-3xl" />
      </div>

      <div className="page-container py-16 md:py-20 w-full relative z-10">
        <div className="max-w-3xl mx-auto">
          <div
            className={`transition-all duration-[600ms] ease-out ${
              isVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-8 scale-[0.96]"
            }`}
            style={{ transitionDelay: "0ms" }}
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-wide text-center">
              {channelsPageData.hero.title}
            </h2>
            <div className="flex items-center justify-center mt-4 mb-6">
              <span className="block w-10 h-[2px] bg-accent-400 rounded-full" />
              <span className="block w-16 h-[3px] bg-accent-500 rounded-full mx-2" />
              <span className="block w-10 h-[2px] bg-accent-400 rounded-full" />
            </div>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto text-center leading-relaxed">
              {channelsPageData.hero.subtitle}
            </p>
          </div>

          <div className="mt-10 md:mt-12 space-y-5 md:space-y-6">
            {platforms.map((platform) => (
              <PlatformCard key={platform.name} platform={platform} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function PlatformCard({ platform }: { platform: typeof channelsPageData.platforms[number] }) {
  const { ref, isVisible } = useEntranceAnimation({ threshold: 0.05, sectionIndex: 0 });

  return (
    <div
      ref={ref}
      className={`transition-all duration-[600ms] ease-out ${
        isVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-12 scale-[0.94]"
      }`}
      style={{ transitionDelay: "80ms" }}
    >
      <div
        className={`group flex items-start gap-4 md:gap-5 p-5 md:p-6 rounded-xl border transition-all duration-300 cursor-pointer hover:-translate-y-1 hover:shadow-lg ${
          platform.primary
            ? "bg-primary-50 border-primary-200 hover:border-primary-400 hover:bg-primary-50/80"
            : "bg-background-50 border-background-200/70 hover:border-accent-300/60 hover:bg-background-50"
        }`}
      >
        <div
          className={`w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-lg transition-all duration-300 group-hover:scale-110 ${
            platform.primary ? "bg-primary-100 text-primary-600" : "bg-accent-100 text-accent-600 group-hover:bg-accent-200 group-hover:text-accent-700"
          }`}
        >
          <i className={`${platform.icon} text-xl`}></i>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-heading text-base font-semibold text-foreground-950 group-hover:text-accent-700 transition-colors duration-300">
              {platform.name}
            </h3>
            {platform.primary && (
              <span className="px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-full whitespace-nowrap">
                主要通路
              </span>
            )}
          </div>
          <p className="text-sm text-foreground-600 leading-relaxed mb-3">
            {platform.description}
          </p>
          <a
            href={platform.url}
            target="_blank"
            rel="nofollow noopener"
            className={`inline-flex items-center gap-1.5 text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap group-hover:gap-2.5 ${
              platform.primary ? "text-primary-600 hover:text-primary-700" : "text-accent-600 hover:text-accent-700"
            }`}
          >
            前往平台
            <i className="ri-arrow-right-up-line text-xs transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"></i>
          </a>
        </div>
      </div>
    </div>
  );
}

function BottomCtaBlock() {
  const navigate = useNavigate();
  const { bottomCta } = channelsPageData;
  const { ref, isVisible } = useEntranceAnimation({ threshold: 0.15, sectionIndex: 1 });

  return (
    <section
      ref={ref}
      className="relative flex items-center justify-center bg-background-100 overflow-hidden"
      style={{ minHeight: "60svh" }}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-72 h-72 rounded-full bg-accent-100/20 blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full bg-primary-100/15 blur-3xl" />
      </div>

      <div className="page-container py-16 md:py-20 text-center relative z-10">
        <div
          className={`transition-all duration-[600ms] ease-out ${
            isVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-8 scale-[0.96]"
          }`}
          style={{ transitionDelay: "0ms" }}
        >
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-wide mb-3">
            {bottomCta.title}
          </h2>
          <div className="flex items-center justify-center mb-6">
            <span className="block w-8 h-[2px] bg-accent-400 rounded-full" />
            <span className="block w-12 h-[3px] bg-accent-500 rounded-full mx-2" />
            <span className="block w-8 h-[2px] bg-accent-400 rounded-full" />
          </div>
          <p className="text-sm md:text-base text-foreground-600 max-w-lg mx-auto mb-6 md:mb-8 leading-relaxed">
            {bottomCta.description}
          </p>
        </div>
        <div
          className={`flex flex-col sm:flex-row items-center justify-center gap-3 transition-all duration-[600ms] ease-out ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
          style={{ transitionDelay: "120ms" }}
        >
          {bottomCta.buttons.map((btn) => (
            <button
              key={btn.label}
              onClick={() => {
                window.scrollTo(0, 0);
                navigate(btn.href);
              }}
              className="px-6 py-3 text-sm font-medium rounded-md transition-all duration-300 cursor-pointer whitespace-nowrap bg-secondary-500 text-background-50 hover:bg-secondary-600 hover:-translate-y-0.5 hover:shadow-md"
            >
              {btn.label}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function StoresPage() {
  const storeItems = channelsPageData.platforms.map((p) => ({ name: p.name, url: p.url }));

  return (
    <div className="bg-background-50">
      <StoresCollectionPageJsonLd stores={storeItems} />
      <StoresItemListJsonLd stores={storeItems} />
      <StoresBreadcrumbJsonLd />
      <Navbar />
      <main>
        <StoresHero />
        <StoresSection />
        <BottomCtaBlock />
      </main>
      <Footer />
    </div>
  );
}